import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, GuardsCheckEnd, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { BasicModalService, FetchMenuService, LayoutDataModel, ModalConfig, SideNavConfig, SideNavConfigItem, SideNavService, AppLayoutComponent } from '@cws-ui/ngx-cws-app-layout';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, Subscription, catchError, of, switchMap } from 'rxjs';
import { common_urls, stp_cws_urls } from 'src/environments/environment';
import { GasaFundEnhancementService } from './gasa-fund-enhancement/services/gasa-fund-enhancement.service';
import { TRACK_LINK_LABELS, TRACK_LINK_TYPE, TRACK_POPUP_TITLE, procedureRequestStatus } from './gasa-fund-enhancement/shared/constants';
import { ErrorModalComponent } from './gasa-fund-enhancement/shared/error-modal/error-modal.component';
import { GasaAdobeAnalytics } from './gasa-fund-enhancement/shared/gasa-adobe-analytics';
import { TrackLinkParams } from './gasa-fund-enhancement/shared/models/gasa-adobe-analytics-model';
import { CwsTitleService } from './service/cws-title.service';
import { LoaderService } from './service/loader.service';
import { CwsUrlNavigator } from './shared/cws-url-navigator';
import { ErrorFaqModalMsgs, ProgressModalMsgs } from './shared/side-menu-popup/enums/error-faq-modal-msgs.enum';
import { CancellationErrorCodes, LoansFundWithdrawalsErrorCodes, ProcedurePremiumInsuranceErrorCodes, ReissueErrorCodes } from './shared/side-menu-popup/enums/side-menu-error-codes.enum';
import { Cancellation, ChangeContractInformation, ProceduresInsurancePremiumPayment, ReissueProcedure, SideMenuItems } from './shared/side-menu-popup/enums/side-menu-items.enums';
import { SFDCDirectNavigationEventEnvBasedUrlMapping, SFDCDirectNavigationEventUrlMapping, SFDCNavigationEventUrlMapping } from './shared/side-menu-popup/side-menu-constants';
import { SideMenuPopupService } from './shared/side-menu-popup/side-menu-popup.service';
import { C360LayoutData } from './shared/side-nav-config/c360-config';
import { EtaxLayoutData } from './shared/side-nav-config/etax.config';
import { FraLayoutData, fraScreenType } from './shared/side-nav-config/fra-config';
import { GasaLayoutData, GasaScreenType } from './shared/side-nav-config/gasa-config';
import { StpLayoutData, StpScreenType } from './shared/side-nav-config/stp.config';
import { create_UUID } from './utils/commons';
import { CWSFontsService } from './service/cws-font.service';
import { CWS_ANGULAR } from './left-menu-items';
import { CWSInactivityService } from './service/cws-inactivity.service';
import { CreditCardService } from './cws2.0/credit-card/services/credit-card.service';
import { AddressTelephoneChangeService } from './cws2.0/address-telephone-change/services/address-telephone-change.service';
import { BankAccountChangeService } from './cws2.0/bank-account-change/services/bank-account-change.service';
import { CreditCardLayoutConfig } from './shared/side-nav-config/credit-card-config';
import { FfDrawerLayoutConfig } from './shared/side-nav-config/ff-drawer-config';
import { ContractLoanLayoutConfig } from './shared/side-nav-config/contract-loan-config';
import { SecuritiesReissueLayoutConfig } from './shared/side-nav-config/securities-reissue-config';
import { FixedPremiumChangeLayoutConfig } from './shared/side-nav-config/fixed-premium-change-config';
import { SurrenderRequestFormMailingLayoutConfig } from './shared/side-nav-config/surrender-request-form-mailing-config';
import { SURRENDER_REQUEST_FORM_MAILING_BASE_ROUTE } from './cws2.0/surrender-request-form-mailing/surrender-request-form-mailing.constants';
import { BankAccountChangeLayoutConfig } from './shared/side-nav-config/bank-account-change-config';
import { FamilyNameChangeProcedureLayoutConfig } from './shared/side-nav-config/family-name-change-procedure-config';
import { FirstNameChangeLayoutConfig } from './shared/side-nav-config/first-name-change-config';
import { AddressTelephoneChangeLayoutConfig } from './shared/side-nav-config/address-telephone-change-config';
import { TaxDeductionCertificateLayoutConfig } from './shared/side-nav-config/tax-deduction-certificate-config';
import { NewSurrenderModalComponent } from './shared/new-surrender-modal/new-surrender-modal.component';
import { UrlHistoryService } from './service/url-history.service';
import { RpSurrenderLayoutData } from './shared/side-nav-config/rp-surrender-config';

import { LoaderComponent } from './shared/loader/loader.component';
import { LogoutButtonComponent } from './shared/logout-button/logout-button.component';
import { HeaderComponent } from './shared/header/header.component';
import { CwsHeaderComponent } from './shared/cws-header/cws-header.component';
import { FooterComponent } from './shared/footer/footer.component';
import { CwsFooterComponent } from './shared/cws-footer/cws-footer.component';
import { SessionTransactionService } from './service/session-transaction.service';
import { BANK_ACCOUNT_CHANGE_BASE_PATH, BANK_ACCOUNT_CHANGE_ROUTES, BANK_ACCOUNT_CHANGE_SCREEN_TYPE } from './cws2.0/bank-account-change/bank-account-change.constants';
import { FAMILY_NAME_CHANGE_PROCEDURE_BASE_ROUTE, FAMILY_NAME_CHANGE_PROCEDURE_SCREEN_TYPE } from './cws2.0/family-name-change-procedure/family-name-change-procedure.constants';
import { FIRST_NAME_CHANGE_BASE_ROUTE, FIRST_NAME_CHANGE_SCREEN_TYPE } from './cws2.0/first-name-change/first-name-change.constants';
import { ADDRESS_TELEPHONE_CHANGE_BASE_ROUTE, ADDRESS_TELEPHONE_CHANGE_SCREEN_TYPE } from './cws2.0/address-telephone-change/address-telephone-change.constants';
import { TAX_DEDUCTION_CERTIFICATE_BASE_ROUTE, TAX_DEDUCTION_CERTIFICATE_SCREEN_TYPE } from './cws2.0/tax-deduction-certificate/tax-deduction-certificate.constants';
import { ProcedureConfigService } from './service/procedure-config.service';
import { PROCEDURE_CONFIG_NAMES, ProcedureBackend } from './service/procedure-config.constants';
import { CWS_HOME_URL } from './shared/guards/procedure-config.guard';
import { feature_flags } from './feature-flags';
import { UserIdChangeLayoutConfig } from './shared/side-nav-config/user-id-change.config';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [RouterOutlet, LoaderComponent, AppLayoutComponent, LogoutButtonComponent, HeaderComponent, CwsHeaderComponent, FooterComponent, CwsFooterComponent, TranslateModule]
})

export class AppComponent implements OnInit, OnDestroy {
  private readonly creditCardScreenType = 'credit-card';
  private readonly ffDrawerScreenType = 'ff-drawer';
  private readonly contractLoanScreenType = 'contract-loan';
  private readonly securitiesReissueScreenType = 'securities-reissue';
  private readonly surrenderRequestFormMailingScreenType = 'surrender-request-form-mailing';
  private readonly familyNameChangeProcedureScreenType = FAMILY_NAME_CHANGE_PROCEDURE_SCREEN_TYPE;
  private translateService = inject(TranslateService);
  private modalService = inject(BasicModalService);
  private router = inject(Router);
  private oidcSecurityService = inject(OidcSecurityService);
  private fetchMenuService = inject(FetchMenuService);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private sideMenuPopupService = inject(SideMenuPopupService);
  private dialog = inject(MatDialog);
  private gasaService = inject(GasaFundEnhancementService);
  matDialog = inject(MatDialog);
  private loaderService = inject(LoaderService);
  private cwsTitleService = inject(CwsTitleService);
  private cwsFontsService = inject(CWSFontsService);
  private cwsInactivityService = inject(CWSInactivityService);
  private creditCardService = inject(CreditCardService);
  private addressTelephoneChangeService = inject(AddressTelephoneChangeService);
  private bankAccountChangeService = inject(BankAccountChangeService);
  private sideNavService = inject(SideNavService);
  private urlHistoryService = inject(UrlHistoryService);
  private sessionTransactionService = inject(SessionTransactionService);
  private procedureConfigService = inject(ProcedureConfigService);


  @ViewChild('error1Template') error1Template!: TemplateRef<any>;
  @ViewChild('error2Template') error2Template!: TemplateRef<any>;
  @ViewChild('error3Template') error3Template!: TemplateRef<any>;
  statusCode!: string;

  isCWSPage: boolean = false
  isAuth: boolean = false;
  isUnauthorized:boolean=false;
  isUserIdChangePage: boolean = false;

  sideNavAllowedRoutes: string[] = [
    '/ereport/index',
    '/ereport/basic-information',
    '/ereport/accumulation-status',
    '/ereport/exchange-rate',
    '/ereport/recipient',
    '/gasa-fund/policy-details',
    '/gasa-fund/fund-switching/disclaimer',
    '/gasa-fund/fund-switching/switching',
    '/gasa-fund/fund-switching/confirmation',
    '/gasa-fund/gasa-cancellation/confirm-cancellation',
    '/gasa-fund/gasa-cancellation/billing-details-entry',
    '/gasa-fund/gasa-cancellation/confirm-billing-details',
    '/gasa-fund/surrender-request-sent',
    '/policy-download'
  ]

  showLogoutButton: boolean = false;
  readonly sessionTransactionId = this.sessionTransactionService.sessionTransactionId;

  // dynamic config for side nav
  layoutData!: LayoutDataModel;

  screenType!: string;
  errorFaqModalMsgText: string = "";
  progressModalText: string = "";
  id: string = "";
  popupModalSubscribe!: Subscription;
  @ViewChild("showFamilyNameMsg") showFamilyNameMsg!: TemplateRef<any>;
  @ViewChild("addressChange") addressChange!: TemplateRef<any>;
  @ViewChild("errorFaqModal") errorFaqModal!: TemplateRef<any>;
  @ViewChild("progressModal") progressModal!: TemplateRef<any>;
  @ViewChild("taxCertificate") taxCertificate!: TemplateRef<any>;
  @ViewChild("cancelContractChange") cancelContractChange!: TemplateRef<any>;

  // dynamic config for side nav
  isMortgage = false;
  isMobileReg = false;
  isCreditCardPage = false;
  isEtaxCertificatePage = false;
  @ViewChild("creditCardChange") creditCardChange!: TemplateRef<any>;
  constructor() {
    this.translateService.setDefaultLang('ja');
    this.translateService.use('ja');
  }

  ngOnInit() {
    let policyNumber: string, smallProdCD: string;
    this.route.queryParamMap.subscribe((res: any) => {
      let params = res['params'];
      CwsUrlNavigator.policyNumber = policyNumber = params['polNo'] ? encodeURIComponent(params['polNo']) : this.gasaService.policyNumber;
      if (params['sprodCd']) {
        smallProdCD = encodeURIComponent(params['sprodCd']);
      }
      else if (params['sprodCode']) {
        smallProdCD = encodeURIComponent(params['sprodCode']);
      }
      else {
        smallProdCD = this.gasaService.smallProdCD || '';
      }
      CwsUrlNavigator.sProdCd = smallProdCD;
      CwsUrlNavigator.lProdCd = params['lprodCd']
        ? encodeURIComponent(params['lprodCd'])
        : params['lprodCode']
          ? encodeURIComponent(params['lprodCode'])
          : this.gasaService.largeProdCD || null;
      CwsUrlNavigator.id = this.id = params["polId"] ? params['polId'] : this.gasaService.id;
    });
    this.cwsTitleService.startMonitoring();
    this.cwsFontsService.startMonitoring();
    this.cwsInactivityService.startMonitoring();
    this.urlHistoryService.startTracking();
    
    this.router.events.subscribe((evt: any) => {
      if ((evt instanceof NavigationEnd)) {
        this.isUserIdChangePage = evt.url.includes('/cws-user-id-change');

        if(evt.url.includes('/unauthorized')){
          this.isUnauthorized=true;
        }
        else if (evt.url.includes('/cws-registration/landing-page') || evt.url.includes("/stop-mailing-certificates") || evt.url.includes("/mobile-registration")) {
          this.isCWSPage = true;
          if (evt.url.includes("/stop-mailing-certificates") || evt.url.includes("/mobile-registration"))
            this.isMortgage = true;
          else
            this.isMortgage = false;
          if (evt.url.includes("/mobile-registration"))

            this.isMobileReg = true;
          else
            this.isMobileReg = false;

          // Check if credit card page
          this.isCreditCardPage = evt.url.includes("/credit-card");
          this.isEtaxCertificatePage = evt.url.includes("/etax/certificate-preview");
        }
        else {
          this.isCWSPage = false; // gasa, fra, c360, etax
          
          // Check if credit card page
          this.isCreditCardPage = evt.url.includes("/credit-card");
          this.isEtaxCertificatePage = evt.url.includes("/etax/certificate-preview");

          this.setSideMenuConfig();
          const isAllowed = this.sideNavAllowedRoutes.some(sideNaveRoute => {
          const urlItems = evt.url.split("?")
          return urlItems[0] === sideNaveRoute && this.isAuth
          });

          if (isAllowed)
            this.fetchSideMenu(policyNumber, smallProdCD); // gasa, fra
          else if (this.layoutData)
            this.layoutData.showSideNav = false; // c360
        }
      }
      if (evt instanceof GuardsCheckEnd) {
        this.isAuth = evt.shouldActivate
      }
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0)
    });

    this.gasaService.accumulatedFundItemLeftMenu.subscribe(response => {
      if (response) {
        this.statusCode = Object(response.metadata)?.fundSwitchingErrorCode?.toString();
      }
    })
      this.oidcSecurityService.checkAuth().subscribe(({ accessToken }) => {
        console.log("app authenticated", accessToken);
        if (accessToken) {
          this.getSessionId();
        }
      });
    if (this.creditCardService.getSelectedPolicies().length > 0) {
        this.clearSelectedCreditCardPolicies();
      }
    // The draft survives app reloads in sessionStorage; the in-memory contracts cache does not.
    // Preserve an explicit refresh, but discard the previous draft on a fresh app visit.
    const isPageReload = performance.getEntriesByType('navigation')
      .some((entry) => (entry as PerformanceNavigationTiming).type === 'reload');
    if (!isPageReload) {
        this.clearStoredAddressTelephoneChangeData();
      }
    // Skip when landing directly on confirmation: the digital NTT gateway redirects back here via a full
    // page reload, and the confirmation page still needs the selected policies/combined-debit preference.
    // Hash-based routing (see app-routing.module.ts useHash: true) means the route lives in location.hash, not pathname.
    const isReturningToBankAccountChangeConfirmation = window.location.hash.includes(
      `${BANK_ACCOUNT_CHANGE_BASE_PATH}/${BANK_ACCOUNT_CHANGE_ROUTES.CONFIRMATION}`
    );
    if (!isReturningToBankAccountChangeConfirmation && this.bankAccountChangeService.getSelectedPolicies().length > 0) {
        this.clearStoredBankAccountChangeData();
      }
  }

  /**
 * set side menu confiugration dynamically as per projects requirements
 */
  setSideMenuConfig() {
    if (this.router.url.includes("/policy-download")) {
      this.showLogoutButton = true;
      this.layoutData = StpLayoutData;
      this.screenType = StpScreenType;
    } else if (this.router.url.includes("/stp-cws-registration")) {
      this.showLogoutButton = false;
      this.layoutData = StpLayoutData;
    } else if (this.router.url.includes("/etax/")) {
      this.layoutData = EtaxLayoutData;
    } else if (this.router.url.includes("gasa-fund/")) {
      this.layoutData = GasaLayoutData;
      this.screenType = GasaScreenType;
    } else if (this.router.url.includes("ereport/")) {
      this.layoutData = FraLayoutData;
      this.screenType = fraScreenType;
    }
    else if (this.router.url.includes("/etax/")) {
      this.layoutData = EtaxLayoutData;
    }
    else if (this.router.url.includes("/family-registration/")) {
      this.layoutData = C360LayoutData;
    }else if (this.router.url.includes("/rp-surrender/")) {
      this.layoutData = RpSurrenderLayoutData;
    } else if (this.router.url.includes("/credit-card")) {
      this.layoutData = CreditCardLayoutConfig;
      this.screenType = this.creditCardScreenType;
    } else if (this.router.url.includes('/ff-drawer')) {
      this.layoutData = FfDrawerLayoutConfig;
      this.screenType = this.ffDrawerScreenType;
    } else if (this.router.url.includes('/contract-loan')) {
      this.layoutData = ContractLoanLayoutConfig;
      this.screenType = this.contractLoanScreenType;
    } else if (this.router.url.includes('/securities-reissue')) {
      this.layoutData = SecuritiesReissueLayoutConfig;
      this.screenType = this.securitiesReissueScreenType;
    } else if (this.router.url.includes('/fixed-premium-change')) {
      this.layoutData = FixedPremiumChangeLayoutConfig;
    } else if (this.router.url.includes('/surrender-request-form-mailing')) {
      this.layoutData = SurrenderRequestFormMailingLayoutConfig;
      this.screenType = this.surrenderRequestFormMailingScreenType;
    } else if (this.router.url.includes('/bank-account-change')) {
      this.layoutData = BankAccountChangeLayoutConfig;
      this.screenType = BANK_ACCOUNT_CHANGE_SCREEN_TYPE;
    } else if (this.router.url.includes('/first-name-change')) {
      this.layoutData = FirstNameChangeLayoutConfig;
      this.screenType = FIRST_NAME_CHANGE_SCREEN_TYPE;
    } else if (this.router.url.includes('/' + FAMILY_NAME_CHANGE_PROCEDURE_SCREEN_TYPE)) {
      this.layoutData = FamilyNameChangeProcedureLayoutConfig;
      this.screenType = FAMILY_NAME_CHANGE_PROCEDURE_SCREEN_TYPE;
    } else if (this.router.url.includes('/address-telephone-change')) {
      this.layoutData = AddressTelephoneChangeLayoutConfig;
      this.screenType = ADDRESS_TELEPHONE_CHANGE_SCREEN_TYPE;
    } else if (this.router.url.includes('/tax-deduction-certificate')) {
      this.layoutData = TaxDeductionCertificateLayoutConfig;
      this.screenType = TAX_DEDUCTION_CERTIFICATE_SCREEN_TYPE;
    } else if(this.router.url.includes("cws-user-id-change")){
      this.layoutData = UserIdChangeLayoutConfig;
    }
    CwsUrlNavigator.screenType = this.screenType;
  }

  handleMenuClick(event: any) {
    // Static Navigation to other URL specified in SFDCDirectNavigationEventUrlMapping
    if (Object.keys(SFDCDirectNavigationEventUrlMapping).includes(event.target)) {
      const navigationUrl = SFDCDirectNavigationEventUrlMapping[event.target];
      window.open(navigationUrl, "_blank");
    }
    // Static SFDC env specific Navigation 
    else if (Object.keys(SFDCDirectNavigationEventEnvBasedUrlMapping).includes(event.target)) {
      const baseUrl = common_urls.cwsBaseUrl;
      const subPath = SFDCDirectNavigationEventEnvBasedUrlMapping[event.target];
      if (event.target == "inquiryItem") {
        // in case of STP , we only need to pass from=home param
        if (this.screenType == StpScreenType) {
          window.open(`${baseUrl}/${subPath}home`, "_self");
        }
        else {
          window.open(`${baseUrl}/${subPath}policyinquiry&id=${this.id}`, "_self");
        }
      } else if (event.target == "contractDocumentsItem") {
        this.router.navigate(["/policy-download"]);
      }
      else {
        window.open(`${baseUrl}/${subPath}`, "_self");
      }
    }
    else if (event.target == "medicalReliefContactSubItem") {
      const menuItem = this.layoutData?.leftMenuItems?.leftMenuItems.filter(x => x?.name == "productIncidentalServiceItem")[0];
      const submenuItem = menuItem?.subItems?.filter(x => x?.name == "medicalReliefContactSubItem")[0];
      const resourcePath = submenuItem?.metadata?.resourcePath;
      if (resourcePath)
        window.open(resourcePath, "_blank")
    }
    else if (event.target == "accumulatedFundTransferSwitchingItem") {
      this.redirectToFundSwitching();
    }
    // C360 internal navigation on menu click event
    else if (event.target == "rakuRakuFamilyRegistrationSubItem") {
      const polNo = CwsUrlNavigator.policyNumber || '';
      const sprodCd = CwsUrlNavigator.sProdCd || '';
      const lprodCd = CwsUrlNavigator.lProdCd || '';
      const id = CwsUrlNavigator.id || '';
      const queryParam = { "sprodCd": sprodCd, "lprodCd": lprodCd, "polNo": polNo, "id": id };
      this.redirectToURL("family-registration/home", true, queryParam);
    }
    // Menu event handling.
    else {
      const subMenu = this.fetchMenuService.findSideNavItem(this.layoutData.leftMenuItems.leftMenuItems, event.target);
      if (subMenu)
        // event.parent is referencing parent of clicked submenu.
        switch (event.parent) {
          case SideMenuItems.ChangeContractInformation:
            this.showContractInformation(event.target, subMenu);
            break;
          case SideMenuItems.Cancellation:
            this.showCancellation(event.target, subMenu);
            break;
          case SideMenuItems.ReissueProcedure:
            this.showReIssueProcedure(event.target, subMenu);
            break;
          case SideMenuItems.ProceduresInsurancePremiumPayment:
            this.showProcedurePremiumInsurancePayment(event.target, subMenu);
            break;
          case SideMenuItems.LoansFundWithdrawals:
            this.showLoansFundWithdrawals(event.target, subMenu);
            break;
        }
    }
  }

  private clearSelectedCreditCardPolicies(): void {
    this.creditCardService.clearSelectedPolicies();
  }

  // Drops stale cached contracts left over from a previous app session so a fresh load never reuses old data.
  private clearStoredAddressTelephoneChangeData(): void {
    this.addressTelephoneChangeService.clear();
  }

  // Drops stale selected policies left over from a previous app session so a fresh load never reuses old data.
  private clearStoredBankAccountChangeData(): void {
    this.bankAccountChangeService.clearState();
  }

  /**
  * Following method is related to gasa fund switching and should not be removed as It would impact the gasa fund switching functionality.
  * The puspose of this method is to construct the navigation url which would be used to navigate back to SFDC site.
  * @param eventName : when we click on one of the menu items in the side navigation bar, the event is represented by event parameter here.
  */
  getNevigationUrl(eventName: string) {
    const baseUrl = common_urls.cwsBaseUrl;
    const subPath = SFDCNavigationEventUrlMapping[eventName];
    const polNo = CwsUrlNavigator.policyNumber;//this.gasaService.policyNumber;
    const sprodCd = CwsUrlNavigator.sProdCd;//this.gasaService.smallProdCD;
    const lprodCd = CwsUrlNavigator.lProdCd;//this.gasaService.largeProdCD;
    const id = CwsUrlNavigator.id;//this.gasaService.id;
    return `${baseUrl}/${subPath}?from=policyinquiry&source=gasafundswitching&cws=1&sprodCd=${sprodCd}&lprodCd=${lprodCd}&polNo=${polNo}&id=${id}`;
  }

  /**
   * check whether error code comes among the values mentioned and accordingly opens corresponding error dialog.
   * If not, redirects to fund switching page.
   */
  redirectToFundSwitching() {
    // show apprpriate error messages in the dialog as per the status code received from api
    if (this.statusCode && Object.values(procedureRequestStatus).map(String).includes(this.statusCode)) {
      switch (this.statusCode) {

        // status: We cannot accept requests made within 2 business days of the date the company received your previous switching request.
        case procedureRequestStatus.CWS001.toString():
          this.openErrorDialog(this.error1Template);
          GasaAdobeAnalytics.trackPopup(TRACK_POPUP_TITLE.errorCWS001);
          break;

        // status: As we have already accepted cancellation procedures, we cannot accept switching procedures.
        case procedureRequestStatus.CWS002.toString():
          this.openErrorDialog(this.error2Template);
          GasaAdobeAnalytics.trackPopup(TRACK_POPUP_TITLE.errorCWS002);
          break;

        // status: We cannot accept switching procedures as there are similar procedures that have recently been completed.
        case procedureRequestStatus.CWS003.toString():
          this.openErrorDialog(this.error3Template);
          GasaAdobeAnalytics.trackPopup(TRACK_POPUP_TITLE.errorCWS003);
          break;
      }

    }
    else {
      let eventInfo: TrackLinkParams = {
        label: TRACK_LINK_LABELS.fundSwitchLeftMenu
      }
      GasaAdobeAnalytics.trackLinkClick(eventInfo);
      // if all conditions satisfies, redirect to fund-switching page
      this.router.navigate(['gasa-fund/fund-switching/disclaimer'], { relativeTo: this.route });
    }
  }

  redirectToGasaCancellation() {
    this.sideNavService.closeSideNav();
    // redirect to gasa cancellation page 
    this.router.navigate(['gasa-fund/gasa-cancellation/confirm-cancellation'], {
      relativeTo: this.route,
      queryParams: {
        polNo: this.gasaService.policyNumber,
        sprodCd: this.gasaService.smallProdCD,
        lprodCd: this.gasaService.largeProdCD,
        polId: this.gasaService.id,
        source: CWS_ANGULAR
        // if required add polId and gasabackUrl as well in queryParams
      }
    });
  }

  /**
   * Opens error dialog for specific error codes
   * @param bodyTemplate error template  which needs to be shown in the error dialog
   */
  openErrorDialog(bodyTemplate: TemplateRef<any>) {
    this.matDialog.open(ErrorModalComponent, {
      data: {
        customBody: bodyTemplate,
      },
      autoFocus: false, // changes for GFE-1633
      restoreFocus: false,  // changes for GFE-1633
      disableClose: true,  // changes for GFE-1632
    }).afterClosed().subscribe((response) => {
    })
  }

  /**
 * Following method is related to show popup or navigate as per provided  business logic for all sub-menus of contract information changes and should not be removed.
 * @param sideMenuItem : when we click on one of the menu items in the side navigation bar, that menu item  is represented by sideMenuItem parameter here.
 * @param subMenu : This param hold all the information and properties for the clicked submenu item.
 */
  showContractInformation(sideMenuItem: string, subMenu: SideNavConfigItem) {
    // FAQ restrictions must take precedence over the Angular/SFDC procedure backend.
    if (
      sideMenuItem === ChangeContractInformation.ChangeNameBeneficiaryDesignatedProxyClaimantContractorInvoiceOrder &&
      ['CWSDIS001', 'CWSDIS002', 'CWSDIS003', 'CWSDIS004'].includes(subMenu.metadata?.['displayRightHolderMsg'])
    ) {
      this.showContractInformationViaSfdcPopup(sideMenuItem, subMenu);
      return;
    }
    if (this.resolveProcedureNavigation(sideMenuItem, subMenu, (item, menu) => this.showContractInformationViaSfdcPopup(item, menu))) {
      return;
    }
    this.showContractInformationViaSfdcPopup(sideMenuItem, subMenu);
  }

  private readonly procedureMenuItemConfig: Record<string, {
    procedureName: string;
    angularRoute: string;
    forwardPolicyContext?: boolean;
  }> = {
    [ChangeContractInformation.ChangeNamePolicyHolderInsuredInvoiceOrder]: {
      procedureName: PROCEDURE_CONFIG_NAMES.FAMILY_NAME_CHANGE,
      angularRoute: `/${FAMILY_NAME_CHANGE_PROCEDURE_BASE_ROUTE}`,
    },
    [ChangeContractInformation.ChangeNameBeneficiaryDesignatedProxyClaimantContractorInvoiceOrder]: {
      procedureName: PROCEDURE_CONFIG_NAMES.OWNER_NAME_CHANGE,
      angularRoute: `/${FIRST_NAME_CHANGE_BASE_ROUTE}`,
      forwardPolicyContext: true,
    },
    [ChangeContractInformation.ChangeAddressPhoneNumber]: {
      procedureName: PROCEDURE_CONFIG_NAMES.ADDRESS_TEL_CHANGE,
      angularRoute: `/${ADDRESS_TELEPHONE_CHANGE_BASE_ROUTE}`,
    },
    [ProceduresInsurancePremiumPayment.ChangeTransferAccount]: {
      procedureName: PROCEDURE_CONFIG_NAMES.BANK_ACCOUNT_CHANGE,
      angularRoute: BANK_ACCOUNT_CHANGE_BASE_PATH,
    },
    [Cancellation.CancellationInvoiceRequest]: {
      procedureName: PROCEDURE_CONFIG_NAMES.SURRENDER_REQUEST_FORM_MAILING,
      angularRoute: `/${SURRENDER_REQUEST_FORM_MAILING_BASE_ROUTE}`,
      forwardPolicyContext: true,
    },
    [ReissueProcedure.MailDeductionCertificate]: {
      procedureName: PROCEDURE_CONFIG_NAMES.INSURANCE_PREMIUM_DEDUCTION_CERTIFICATE,
      angularRoute: `/${TAX_DEDUCTION_CERTIFICATE_BASE_ROUTE}`,
    },
  };

  /**
   * Kill switch: looks up sideMenuItem in procedureMenuItemConfig, asks the BFF for its Angular/SFDC
   * backend, and navigates accordingly; returns false when there's no mapping so the caller falls back
   * to its existing SFDC popup logic unchanged.
   */
  private resolveProcedureNavigation(
    sideMenuItem: string,
    subMenu: SideNavConfigItem,
    legacyHandler: (sideMenuItem: string, subMenu: SideNavConfigItem) => void
  ): boolean {
    const menuItemConfig = this.procedureMenuItemConfig[sideMenuItem];
    if (!menuItemConfig) {
      return false;
    }

    this.loaderService.show();
    this.procedureConfigService.load(menuItemConfig.procedureName).subscribe({
      next: (config) => {
        this.loaderService.hide();
        if (!config?.enabled) {
          window.location.href = CWS_HOME_URL;
        } else if (config.backend === ProcedureBackend.Angular) {
          if (sideMenuItem === ChangeContractInformation.ChangeNamePolicyHolderInsuredInvoiceOrder) {
            const [confirmationConfig] = this.sideMenuPopupService.changeContractInformation(
              sideMenuItem,
              subMenu,
              this.errorFaqModal,
              this.showFamilyNameMsg,
              this.addressChange
            );
            if (confirmationConfig) {
              this.popupModalSubscribe = this.showPopUp(confirmationConfig).subscribe(result => {
                if (result === 'true') {
                  this.router.navigate([menuItemConfig.angularRoute]);
                }
              });
              return;
            }
          }
          // polNo/sprodCd/lprodCd forwarding (when needed) is resolved by the destination procedure
          // itself via CwsUrlNavigator.resolvePolicyNumber/resolveSProdCd/resolveLProdCd too, but the
          // current route's own values are preferred here so the router encodes them exactly once.
          const queryParams = menuItemConfig.forwardPolicyContext
            ? {
                polNo: this.route.snapshot.queryParamMap.get('polNo'),
                sprodCd: this.route.snapshot.queryParamMap.get('sprodCd'),
                lprodCd: this.route.snapshot.queryParamMap.get('lprodCd'),
              }
            : undefined;
          this.router.navigate([menuItemConfig.angularRoute], queryParams ? { queryParams } : undefined);
        } else if (config.backend === ProcedureBackend.Sfdc) {
          // Reuse the existing, established SFDC target/URL-form derivation (handles call-center
          // users, confirmation popups, etc.) instead of re-deriving it here.
          legacyHandler(sideMenuItem, subMenu);
        } else {
          window.location.href = CWS_HOME_URL;
        }
      },
      error: () => {
        this.loaderService.hide();
        window.location.href = CWS_HOME_URL;
      }
    });
    return true;
  }

  showContractInformationViaSfdcPopup(sideMenuItem: string, subMenu: SideNavConfigItem) {
    // As per the business logic, this method is returning in-case of popup : pop-up config with redirection url ,if not then redirection url.
    const [config, redirectUrl] = this.sideMenuPopupService.changeContractInformation(sideMenuItem, subMenu, this.errorFaqModal, this.showFamilyNameMsg, this.addressChange)
    if (!config) {
      if (redirectUrl)
        this.redirectToURL(redirectUrl)
    }
    else {
      if (sideMenuItem == ChangeContractInformation.ChangeNameBeneficiaryDesignatedProxyClaimantContractorInvoiceOrder && subMenu.metadata) {
        this.errorFaqModalMsgText = ErrorFaqModalMsgs[subMenu.metadata["displayRightHolderMsg"] as keyof typeof ErrorFaqModalMsgs];
      }
      this.popupModalSubscribe = this.showPopUp(config).subscribe(result => {
        if (result == "true") {
          if (redirectUrl) {
            this.redirectToURL(redirectUrl)
          }
        }
        // else , popup will be closed with no action
      });
    }
  }

  /**
  * Following method is related to show popup or navigate as per provided  business logic for all sub-menus of Cancellation and should not be removed.
  * @param sideMenuItem : when we click on one of the menu items in the side navigation bar, that menu item  is represented by sideMenuItem parameter here.
  *  @param subMenu : This param hold all the information and properties for the clicked submenu item.
  */

  showCancellation(sideMenuItem: string, subMenu: SideNavConfigItem) {
    if (this.resolveProcedureNavigation(sideMenuItem, subMenu, (item, menu) => this.showCancellationViaSfdcPopup(item, menu))) {
      return;
    }
    this.showCancellationViaSfdcPopup(sideMenuItem, subMenu);
  }

  showCancellationViaSfdcPopup(sideMenuItem: string, subMenu: SideNavConfigItem) {
    //handling for gasa cancellation
    if (
      sideMenuItem == Cancellation.CancellationDynamic &&
      [
        CancellationErrorCodes.CWSRWS003,
        CancellationErrorCodes.CWSRWS004,
        CancellationErrorCodes.CWSRWS005,
        CancellationErrorCodes.CWSRWS006,
        CancellationErrorCodes.CWSRWS007,
      ].includes(subMenu?.metadata['popupActionCode'])
    ) {
      if (subMenu?.metadata['cancelContractModal']) {
        this.handleCancellation(subMenu?.metadata['popupActionCode']);
      } else {
        if (
          subMenu?.metadata['popupActionCode'] ===
          CancellationErrorCodes.CWSRWS007
        ) {
          this.redirectToGasaCancellation();
        }
      }
    }
    else {
      // As per the business logic, this method is returning in-case of popup : pop-up config with redirection url ,if not then redirection url.
      // router.url is checked directly (not this.screenType) since screenType isn't reset on every route and can be stale.
      const [config, redirectUrl] = this.sideMenuPopupService.cancellation(
        sideMenuItem,
        subMenu,
        this.errorFaqModal,
        this.progressModal,
        this.cancelContractChange,
        this.router.url.includes('ereport/')
      );
      if (!config) {
        if (redirectUrl) {
          //else navigate outside
          this.redirectToURL(redirectUrl, redirectUrl.startsWith('/'));
        }
      } else {
        if (subMenu.metadata) {
          switch (subMenu.metadata['popupActionCode']) {
            case CancellationErrorCodes.CWSTDSB001:
            case CancellationErrorCodes.CWSRWS001:
            case CancellationErrorCodes.CWSRWS002:
            case CancellationErrorCodes.CWSWSA001:
              this.errorFaqModalMsgText =
                ErrorFaqModalMsgs[
                subMenu.metadata[
                'popupActionCode'
                ] as keyof typeof ErrorFaqModalMsgs
                ];
              break;
            case CancellationErrorCodes.CWSTDSB003:
            case CancellationErrorCodes.CWSRWS004:
              this.progressModalText =
                ProgressModalMsgs[
                subMenu.metadata[
                'popupActionCode'
                ] as keyof typeof ProgressModalMsgs
                ];
              break;
            default:
              break;
          }

          this.popupModalSubscribe = this.showPopUp(config).subscribe(
            (result) => {
              if (result == 'true') {
                if (redirectUrl) {
                  this.redirectToURL(redirectUrl, redirectUrl.startsWith('/'));
                }
              } else if (result == 'cancelContractChange') {
                // if cancel contract is returned then we need to again manipulate as per business Logic
                this.showCancellation(
                  Cancellation.CancellationInvoiceRequest,
                  subMenu
                );
              }
              // else , popup will be closed with no action
            }
          );
        }
      }
    }


  }

  /**
   * Following method is related to show popup or navigate as per provided  business logic for all sub-menus of ReIssue Procedure and should not be removed.
   * @param sideMenuItem : when we click on one of the menu items in the side navigation bar, that menu item  is represented by sideMenuItem parameter here.
   * @param subMenu : This param hold all the information and properties for the clicked submenu item.
   */
  showReIssueProcedure(sideMenuItem: string, subMenu: SideNavConfigItem) {
    if (this.resolveProcedureNavigation(sideMenuItem, subMenu, (item, menu) => this.showReIssueProcedureViaSfdcPopup(item, menu))) {
      return;
    }
    this.showReIssueProcedureViaSfdcPopup(sideMenuItem, subMenu);
  }

  showReIssueProcedureViaSfdcPopup(sideMenuItem: string, subMenu: SideNavConfigItem) {
    // As per the business logic, this method is returning in-case of popup : pop-up config with redirection url ,if not then redirection url.
    const [config, redirectUrl, isInternalNavigation, queryParam] = this.sideMenuPopupService.reissueProcedure(sideMenuItem, subMenu, this.errorFaqModal, this.progressModal, this.taxCertificate);

    if (!config) {
      if (redirectUrl)
        this.redirectToURL(redirectUrl, isInternalNavigation, queryParam);
    }
    else {
      if (subMenu.metadata) {
        if (subMenu.metadata["popupActionCode"] == ReissueErrorCodes.CWSREI001 || subMenu.metadata["popupActionCode"] == ReissueErrorCodes.CWSREI002
          || subMenu.metadata["popupActionCode"] == ReissueErrorCodes.CWSREI008 || subMenu.metadata["popupActionCode"] == ReissueErrorCodes.CWSREI009
          || subMenu.metadata["popupActionCode"] == ReissueErrorCodes.CWSREI010) {
          this.errorFaqModalMsgText = ErrorFaqModalMsgs[subMenu.metadata["popupActionCode"] as keyof typeof ErrorFaqModalMsgs];;
        }
        if (subMenu.metadata["popupActionCode"] == ReissueErrorCodes.CWSREI003 || subMenu.metadata["popupActionCode"] == ReissueErrorCodes.CWSREI004) {
          this.progressModalText = subMenu.metadata["popupActionCode"] == ReissueErrorCodes.CWSREI003 ? "" : "app.common-modal.cancellation.text-3";
        }
        this.popupModalSubscribe = this.showPopUp(config).subscribe(result => {
          if (result == "true") {
            if (redirectUrl) {
              this.redirectToURL(redirectUrl);
            }
          }
          // else , popup will be closed with no action
        });
      }
    }

  }

  /**
    * Following method is related to show popup or navigate as per provided  business logic for all sub-menus of ReIssue Procedure and should not be removed.
    * @param sideMenuItem : when we click on one of the menu items in the side navigation bar, that menu item  is represented by sideMenuItem parameter here.
    * @param subMenu : This param hold all the information and properties for the clicked submenu item.
    */
  showProcedurePremiumInsurancePayment(sideMenuItem: string, subMenu: SideNavConfigItem) {
    if (this.resolveProcedureNavigation(sideMenuItem, subMenu, (item, menu) => this.showProcedurePremiumInsurancePaymentViaSfdcPopup(item, menu))) {
      return;
    }
    this.showProcedurePremiumInsurancePaymentViaSfdcPopup(sideMenuItem, subMenu);
  }

  showProcedurePremiumInsurancePaymentViaSfdcPopup(sideMenuItem: string, subMenu: SideNavConfigItem) {
    // As per the business logic, this method is returning in-case of popup : pop-up config with redirection url ,if not then redirection url.
    const [config, redirectUrl] = this.sideMenuPopupService.procedurePremiumInsurancePayment(sideMenuItem, subMenu, this.errorFaqModal, this.progressModal, this.creditCardChange);

    if (!config) {
      if (redirectUrl) {
        if (redirectUrl.startsWith('/')) {
          this.sideNavService.closeSideNav();
        }
        this.redirectToURL(redirectUrl, redirectUrl.startsWith('/'));
      }
    }
    else {
      if (subMenu.metadata) {
        switch (subMenu.metadata["popupActionCode"]) {
          case ProcedurePremiumInsuranceErrorCodes.CWSBAC001:
          case ProcedurePremiumInsuranceErrorCodes.CWSBAC002:
          case ProcedurePremiumInsuranceErrorCodes.CWSCCC001:
          case ProcedurePremiumInsuranceErrorCodes.CWSURB001:
          case ProcedurePremiumInsuranceErrorCodes.CWSURB002:
          case ProcedurePremiumInsuranceErrorCodes.CWSURB003:
          case ProcedurePremiumInsuranceErrorCodes.CWSURB004:
          case ProcedurePremiumInsuranceErrorCodes.CWSURB005:
          case ProcedurePremiumInsuranceErrorCodes.CWSURB006:
          case ProcedurePremiumInsuranceErrorCodes.CWSURB007:
            this.errorFaqModalMsgText = ErrorFaqModalMsgs[subMenu.metadata["popupActionCode"] as keyof typeof ErrorFaqModalMsgs];
            break;
          case ProcedurePremiumInsuranceErrorCodes.CWSCCC003:
          case ProcedurePremiumInsuranceErrorCodes.CWSURB009:
            this.progressModalText = ProgressModalMsgs[subMenu.metadata["popupActionCode"] as keyof typeof ProgressModalMsgs];
            break;
          default:
            break;
        }

        this.popupModalSubscribe = this.showPopUp(config).subscribe(result => {
          if (result == "true") {
            if (redirectUrl) {
              if (redirectUrl.startsWith('/')) {
                this.sideNavService.closeSideNav();
              }
              this.redirectToURL(redirectUrl, redirectUrl.startsWith('/'));
            }
          }
          // else , popup will be closed with no action
        });
      }
    }

  }

  /**
    * Following method is related to show popup or navigate as per provided  business logic for all sub-menus of Loans fund withdrawls and should not be removed.
    * @param sideMenuItem : when we click on one of the menu items in the side navigation bar, that menu item  is represented by sideMenuItem parameter here.
    * @param subMenu : This param hold all the information and properties for the clicked submenu item.
    */
  showLoansFundWithdrawals(sideMenuItem: string, subMenu: SideNavConfigItem) {
    // As per the business logic, this method is returning in-case of popup : pop-up config with redirection url ,if not then redirection url.
    const [config, redirectUrl] = this.sideMenuPopupService.loansFundWithdrawals(sideMenuItem, subMenu, this.errorFaqModal, this.progressModal);
    if (!config) {
      if (redirectUrl)
        this.redirectToURL(redirectUrl, redirectUrl.startsWith('/'));
    }
    else {
      if (subMenu.metadata) {
        switch (subMenu.metadata["popupActionCode"]) {
          case LoansFundWithdrawalsErrorCodes.CWSUPL001:
          case LoansFundWithdrawalsErrorCodes.CWSUPL005:
          case LoansFundWithdrawalsErrorCodes.CWSUPL006:
            this.errorFaqModalMsgText = ErrorFaqModalMsgs[subMenu.metadata["popupActionCode"] as keyof typeof ErrorFaqModalMsgs];
            break;
          case LoansFundWithdrawalsErrorCodes.CWSUPL002:
          case LoansFundWithdrawalsErrorCodes.CWSUPL003:
            this.progressModalText = ProgressModalMsgs[subMenu.metadata["popupActionCode"] as keyof typeof ProgressModalMsgs];
            break;
          default:
            break;
        }
        this.popupModalSubscribe = this.showPopUp(config).subscribe(result => {
          if (result == "true") {
            if (redirectUrl) {
              this.redirectToURL(redirectUrl, redirectUrl.startsWith('/'));
            }
          }
          // else , popup will be closed with no action
        });
      }
    }

  }

  showPopUp(popUpConfig: ModalConfig): Observable<any> {
    this.dialog.closeAll();
    const modalConfig: ModalConfig = {
      ...popUpConfig,
      customConfig: {
        autoFocus: true,
        restoreFocus: false
      }
    }
    return this.modalService.openModal(modalConfig);
  }

  private fetchSideMenu(policyNumber?: string, smallProdCD?: string) {
    let headers: HttpHeaders;
    const screenType = this.screenType;
    this.loaderService.show();
    let url: string;
    //URL to be updated dynamically
    if (this.screenType === StpScreenType) {
      url = `${stp_cws_urls.oodMenuItemsUrl}/menu-items/${create_UUID()}?screenType=${screenType}`;
    } else {
      url = `${common_urls.oodMenuItemsUrl}/menu-items/${create_UUID()}?policyNumber=${policyNumber}&smallProductCode=${smallProdCD}&screenType=${screenType}`;
    }

    return this.oidcSecurityService.getAccessToken().pipe(
      switchMap((accessToken: string) => {
        headers = new HttpHeaders({
          'Authorization': 'Bearer ' + accessToken
        })
        return this.fetchMenuService.fetchMenu(url, this.http, headers);
      }),
      catchError((err) => {
        this.loaderService.hide();
        console.error(err);
        if (this.layoutData) {
          this.layoutData.showSideNav = false;
        }
        return of()
      })
    )
      .subscribe(leftMenu => {
        //   // this.fetchMenuService.replacePropInHref(left_menu, 'listOfContractsItem', ["homepage"], ["logout"]);
        //   // this.fetchMenuService.findSideNavItem(left_menu.leftMenuItems, 'listOfContractsItem');
        if (this.layoutData) {
          this.layoutData.showSideNav = true;
          this.layoutData.leftMenuItems = leftMenu;
        }
        this.setAccumulatedFundSwitchingConfig(leftMenu);
        this.preCwsUserHomeNavigation(leftMenu);
      });

  }
  setAccumulatedFundSwitchingConfig(leftMenu: SideNavConfig) {
    const data = leftMenu.leftMenuItems.filter(x => x.name == "accumulatedFundTransferSwitchingItem")[0];
    this.gasaService.accumulatedFundItemLeftMenu.next(data);
    this.loaderService.hide();
  }

  // this.layoutData.showSideNav = true;
  // this.layoutData.leftMenuItems = LEFT_MENU_ITEMS;

  preCwsUserHomeNavigation(leftMenu: any) {
    //isStpPreCwsUser will be received only for stp
    if (leftMenu.metadata && leftMenu.metadata.isStpPreCwsUser) {
      const baseUrl = common_urls.cwsBaseUrl;
      const cwsHomeUrl = `${baseUrl}/MLJ_CWS_PreUser_Home?from=auth`;
      StpLayoutData.manulifeWordUrl = cwsHomeUrl;
      StpLayoutData.logoUrl = cwsHomeUrl;
    }
  }

  getSessionId(){
    this.sessionTransactionService.generateSessionID().subscribe((response) => {
      if (response) {
        const w = window as any;
        w._da_ = w._da_ || {};
        w.decibelInsight("sendCustomDimension", "Session Transaction ID", response.sessionTxnId);
        console.log(response,'VOC sessionId')
      }
    })
  }

  ngOnDestroy() {
    if (this.popupModalSubscribe) this.popupModalSubscribe.unsubscribe();
  }

  /**
   * Following method is related to navigate the application either internally to different route or to SFDC navigation
   * @param redirectURL : This value contains the redirection url
   * @param isInternalNavigation : This param defining if the redirect url is internal route or not 
   * @param queryParam : This param conatins the query param object for the redirection url
   */
  redirectToURL(redirectURL: string, isInternalNavigation: boolean | null = false, queryParam: { [key: string]: string } | null = null) {
    if (!isInternalNavigation)
      location.href = redirectURL;
    else
      if (queryParam)
        this.router.navigate([redirectURL], { queryParams: queryParam, relativeTo: this.route });
      else
        this.router.navigateByUrl(redirectURL);
  }

  /*gasa cancellation popup changes*/
  handleCancellation(popupActionCode: string) {
    this.matDialog
      .open(NewSurrenderModalComponent, {
        autoFocus: false,
        disableClose: true,
        panelClass: ['gasa-cancellation-cds-animation'],
        exitAnimationDuration: '0.5s',
      })
      .afterClosed()
      // .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response == "true") {
          if (popupActionCode == CancellationErrorCodes.CWSRWS007) {
            this.redirectToGasaCancellation();
          }
        }
      });
  }
}